/**
 * Path — the fixed lane enemies walk along.
 *
 * Engine-agnostic (no Phaser import) so it can be unit-tested headlessly. A path
 * is a polyline of waypoints; we precompute cumulative segment lengths so that
 * mapping a scalar `distance` -> world {x, y, angle} is cheap. Enemies store only
 * a scalar distance and advance it each tick (see GAME_DESIGN §3.2, no per-frame
 * pathfinding).
 */

export interface Vec2 {
  x: number;
  y: number;
}

export interface PathPoint extends Vec2 {
  /** Facing along the path at this distance, in radians. */
  angle: number;
}

export class Path {
  readonly points: ReadonlyArray<Vec2>;
  /** Cumulative distance from the start to each waypoint index. */
  private readonly cumulative: number[];
  readonly length: number;

  constructor(points: Vec2[]) {
    if (points.length < 2) {
      throw new Error('Path requires at least 2 waypoints');
    }
    this.points = points.map((p) => ({ x: p.x, y: p.y }));
    this.cumulative = [0];
    for (let i = 1; i < points.length; i++) {
      const d = Path.dist(points[i - 1], points[i]);
      this.cumulative.push(this.cumulative[i - 1] + d);
    }
    this.length = this.cumulative[this.cumulative.length - 1];
  }

  static dist(a: Vec2, b: Vec2): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
  }

  /**
   * Position + facing at a given distance along the path. Distances are clamped
   * to [0, length]. O(log n) via binary search over cumulative lengths.
   */
  getPointAt(distance: number): PathPoint {
    const d = Math.max(0, Math.min(distance, this.length));

    // Find segment index `i` such that cumulative[i] <= d <= cumulative[i+1].
    let lo = 0;
    let hi = this.cumulative.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.cumulative[mid + 1] < d) lo = mid + 1;
      else hi = mid;
    }
    const i = Math.min(lo, this.points.length - 2);

    const a = this.points[i];
    const b = this.points[i + 1];
    const segLen = this.cumulative[i + 1] - this.cumulative[i];
    const t = segLen > 0 ? (d - this.cumulative[i]) / segLen : 0;

    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      angle: Math.atan2(b.y - a.y, b.x - a.x),
    };
  }

  /** Convenience: progress 0..1 along the whole path. */
  getPointAtProgress(progress: number): PathPoint {
    return this.getPointAt(progress * this.length);
  }

  /**
   * Distance along the path of the point nearest to `p`. Used to place a melee
   * unit's block position on the lane (its Ground slot sits beside the path) and
   * to reason about which path span a slot covers. O(n) over segments.
   */
  nearestDistance(p: Vec2): number {
    let best = 0;
    let bestD2 = Infinity;
    for (let i = 0; i < this.points.length - 1; i++) {
      const a = this.points[i];
      const b = this.points[i + 1];
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const len2 = abx * abx + aby * aby;
      let t = len2 > 0 ? ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2 : 0;
      t = Math.max(0, Math.min(1, t));
      const cx = a.x + abx * t;
      const cy = a.y + aby * t;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = this.cumulative[i] + Math.sqrt(len2) * t;
      }
    }
    return best;
  }
}
